<template>
  <div class="case-detail-page">
    <banner title="案例详情" subtitle="PROJECT DETAIL" />

    <main class="case-detail" v-loading="loading">
      <router-link class="back-link" to="/case">
        <span aria-hidden="true">←</span>
        返回案例列表
      </router-link>

      <article v-if="hasCase" class="case-article">
        <header class="case-article__header">
          <span class="case-article__eyebrow">PROJECT STORY</span>
          <h2>{{ caseIdList.Title || "案例详情" }}</h2>
          <time v-if="caseIdList.CreateTime" class="case-article__time">
            {{ caseIdList.CreateTime }}
          </time>
        </header>

        <figure v-if="caseIdList.Img" class="case-article__figure">
          <img v-lazy="imgserver + caseIdList.Img" :alt="caseIdList.Title || '案例图片'" />
        </figure>

        <div class="case-article__content">{{ caseIdList.Content }}</div>
      </article>

      <section v-else-if="!loading" class="case-detail-empty" role="status">
        <i class="el-icon-warning-outline" aria-hidden="true"></i>
        <h2>暂时无法查看该案例</h2>
        <p>{{ errorMessage || "案例可能已被调整，请返回列表查看其他项目。" }}</p>
        <el-button type="primary" @click="$router.push('/case')">返回案例列表</el-button>
      </section>
    </main>
  </div>
</template>

<script>
import Banner from "../components/Banner";
import { applySeoForRoute } from "../services/seoClient";

export default {
  components: { Banner },
  data() {
    return {
      loading: true,
      errorMessage: "",
      caseIdList: {}
    };
  },
  computed: {
    hasCase() {
      return Boolean(this.caseIdList && (this.caseIdList.Title || this.caseIdList.Content));
    }
  },
  watch: {
    "$route.params.id": {
      immediate: true,
      handler() {
        this.loadData();
      }
    }
  },
  methods: {
    caseSummary() {
      return String(this.caseIdList.Content || "")
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 120);
    },
    syncCaseSeo(isValid) {
      if (isValid) {
        const caseTitle = String(this.caseIdList.Title || "案例详情").trim();
        const summary = this.caseSummary();
        applySeoForRoute(this.$route, {
          title: `${caseTitle}｜尚亚科技案例`,
          description: summary ? `${caseTitle}：${summary}` : `查看${caseTitle}的项目介绍与建设成果。`,
          heading: caseTitle,
          summary: summary || `查看${caseTitle}的项目介绍与建设成果。`,
          keywords: [caseTitle, "尚亚科技案例", "刚好来商城案例"],
          robots: "index, follow"
        });
        return;
      }
      applySeoForRoute(this.$route, {
        title: "案例暂时不可用｜尚亚科技",
        description: this.errorMessage || "该案例当前不可用，请返回尚亚科技经典案例列表。",
        heading: "案例暂时不可用",
        robots: "noindex, nofollow",
        indexable: false
      });
    },
    loadData() {
      const caseId = this.$route.params.id;
      if (!caseId) {
        this.loading = false;
        this.errorMessage = "未找到有效的案例编号。";
        this.syncCaseSeo(false);
        return;
      }

      this.loading = true;
      this.errorMessage = "";
      this.caseIdList = {};
      this.$http
        .get(`Cases/GetCasesById/${encodeURIComponent(caseId)}`)
        .then(response => {
          this.caseIdList = response.data || {};
          if (!this.hasCase) {
            this.errorMessage = "未找到对应的案例内容。";
          }
          this.syncCaseSeo(this.hasCase);
        })
        .catch(error => {
          this.errorMessage = "案例数据加载失败，请稍后重试。";
          this.syncCaseSeo(false);
          window.console.warn("Failed to load case detail", error);
        })
        .then(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.case-detail-page {
  min-height: 100vh;
  background:
    linear-gradient(180deg, rgba(18, 112, 180, 0.06), transparent 400px),
    var(--color-surface-soft);
}

.case-detail {
  width: min(calc(100% - (var(--page-gutter) * 2)), 1040px);
  min-height: 460px;
  margin: 0 auto;
  padding: clamp(42px, 6vw, 80px) 0 var(--section-space);
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
  color: var(--color-ink-muted);
  font-weight: 700;
  text-decoration: none;
  transition: color 0.2s ease, transform 0.2s ease;

  &:hover {
    color: var(--color-brand);
    transform: translateX(-3px);
  }
}

.case-article {
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(15, 50, 79, 0.08);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
}

.case-article__header {
  padding: clamp(32px, 6vw, 64px) clamp(24px, 7vw, 80px) clamp(28px, 4vw, 46px);
  text-align: center;

  h2 {
    max-width: 760px;
    margin: 12px auto 14px;
    color: var(--color-ink);
    font-size: var(--text-h2);
    font-weight: 700;
    line-height: 1.28;
  }
}

.case-article__eyebrow {
  color: var(--color-brand);
  font-size: var(--text-eyebrow);
  font-weight: 700;
  letter-spacing: var(--tracking-eyebrow);
}

.case-article__time {
  color: var(--color-ink-soft);
  font-size: 14px;
}

.case-article__figure {
  margin: 0;
  padding: 0 clamp(20px, 5vw, 56px);

  img {
    display: block;
    width: 100%;
    max-height: 620px;
    object-fit: contain;
    background: var(--color-surface-soft);
    border-radius: var(--radius-md);
  }
}

.case-article__content {
  padding: clamp(30px, 5vw, 58px) clamp(24px, 7vw, 80px) clamp(46px, 7vw, 80px);
  color: var(--color-ink-muted);
  font-size: 17px;
  line-height: 2;
  text-align: left;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.case-detail-empty {
  display: grid;
  place-items: center;
  min-height: 380px;
  padding: 48px 20px;
  text-align: center;
  background: #fff;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);

  i {
    margin-bottom: 16px;
    color: var(--color-brand);
    font-size: 48px;
  }

  h2 {
    margin: 0 0 10px;
    color: var(--color-ink);
  }

  p {
    margin: 0 0 24px;
    color: var(--color-ink-muted);
  }
}

@media (max-width: 640px) {
  .case-detail {
    width: min(calc(100% - 32px), 1040px);
    padding-top: 32px;
  }

  .case-article__figure {
    padding: 0 16px;
  }

  .case-article__content {
    font-size: 16px;
    line-height: 1.9;
  }
}
</style>
